import socketClient, { Socket } from 'socket.io-client'

import { Logger, TokenProvider } from '../ports'
import { decodeChat, decodeMessageReceivedEvent } from '../protocol/decode'
import { fold } from '../result'
import { ChatSocket, ChatSocketHandlers } from './socket'

export interface SocketConfig {
  readonly host: string
  readonly userId: string
  readonly getToken: TokenProvider
  readonly logger: Logger
}

const REJECTED_HANDSHAKE_RETRY_MS = 5000

export class SocketIoChatSocket implements ChatSocket {
  private socket: Socket = null
  private retryTimer: ReturnType<typeof setTimeout> = null

  public constructor(private readonly config: SocketConfig) {}

  public start(handlers: ChatSocketHandlers): void {
    if (this.socket) {
      throw new Error('ChatSocket.start called twice')
    }

    const { host, userId, getToken, logger } = this.config
    const protocol = host.startsWith('http') ? '' : 'https://'

    const socket = socketClient(`${protocol}${host}`, {
      path: '/chat-service/socket.io/',
      query: { userId },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelayMax: 10000,
      auth: async cb => {
        try {
          cb({ token: await getToken() })
        } catch (e) {
          logger.error(`chat socket token: ${String(e)}`)
          cb({})
        }
      },
    })
    this.socket = socket

    socket.on('connect', handlers.onConnected)
    socket.on('disconnect', handlers.onDisconnected)
    // socket.io only auto-retries transport failures; a handshake the server
    // refused (e.g. auth middleware rejecting a stale token) marks the socket
    // inactive and abandons it. Reconnection is this adapter's contract, so a
    // refusal degrades to a delayed retry instead of permanent death.
    socket.on('connect_error', error => {
      logger.warn(`chat socket connect_error: ${error.message}`)
      if (!socket.active && this.retryTimer === null) {
        this.retryTimer = setTimeout(() => {
          this.retryTimer = null
          socket.connect()
        }, REJECTED_HANDSHAKE_RETRY_MS)
      }
    })

    socket.on('message_received', raw =>
      fold(
        decodeMessageReceivedEvent(raw),
        event => handlers.onMessageReceived(event),
        error => logger.warn(`chat socket message_received: ${error.detail}`),
      ),
    )

    socket.on('chat_created', raw =>
      fold(
        decodeChat(raw),
        chat => handlers.onChatCreated(chat),
        error => logger.warn(`chat socket chat_created: ${error.detail}`),
      ),
    )

    socket.on('chat_updated', raw =>
      fold(
        decodeChat(raw),
        chat => handlers.onChatUpdated(chat),
        error => logger.warn(`chat socket chat_updated: ${error.detail}`),
      ),
    )

    socket.on('contact_joined', raw => {
      if (typeof raw === 'string') {
        handlers.onContactJoined(raw)
      }
    })

    socket.on('contact_left', raw => {
      if (typeof raw === 'string') {
        handlers.onContactLeft(raw)
      }
    })
  }

  public stop(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
    this.socket?.close()
    this.socket = null
  }
}
